import { DashboardLayout } from "components/layout/dashboard";
import { PATH_DASHBOARD, PATH_OTHER } from "config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProfileInfoLoader } from "./ProfileInfoLoader";
import { PaymentStatus } from "store/types/enum";
import { useEffect, useMemo, useState } from "react";
import { PlanPurchaseSuccessResult } from "./results/success/PlanPurchaseSuccessResult";
import { UpgradeSuccessResult } from "./results/success/UpgradeSuccessResultResult";
import { DowngradeSuccessResult } from "./results/success/DowngradeSuccessResult";
import { PlanDowngradeCancelSuccessResult } from "./results/success/PlanDowngradeCancelSuccessResult";
import { PlanDowngradeRequestSuccessResult } from "./results/success/PlanDowngradeRequestSuccessResult";
import { ProjectPurchaseSuccessResult } from "./results/success/ProjectPurchaseSuccessResult";
import { readOtpCheckoutContext, type OtpCheckoutContext } from "./otpCheckoutContext";
import { isOtpPurchaseVerificationReady } from "utils/otpPaymentVerification";
import type { SessionUser } from "types/session-user";

const PARAMS_STATUS = "status";
const PARAMS_PLAN = "plan";
const PARAMS_INTERVAL = "interval";
const PARAMS_COUNT = "count";
const PARAMS_WORKSPACE_ID = "workspace_id";
const PARAMS_PROJECT_ID = "project_id";
/** Stripe replaces this literal in success_url with the Checkout Session id. */
const PARAMS_CHECKOUT_SESSION_ID = "session_id";
const STRIPE_CHECKOUT_SESSION_ID_PLACEHOLDER = "{CHECKOUT_SESSION_ID}";

const OTP_PURCHASE_POLL_MAX_TRIES = 30;
const OTP_PURCHASE_POLL_DELAY_MS = 1000;

export const GetPricingPlanPaymentPageUrl = (
  status:
    | PaymentStatus.CREATE_PLAN_SUBSCRIPTION
    | PaymentStatus.UPGRADE_PLAN_SUBSCRIPTION
    | PaymentStatus.DOWNGRADE_PLAN_SUBSCRIPTION
    | PaymentStatus.CANCEL_DOWNGRADE_PLAN_SUBSCRIPTION,
  plan: number,
  interval: string,
  fullPath: boolean
) => {
  const path = `${PATH_DASHBOARD.payments}?${PARAMS_STATUS}=${status}&${PARAMS_PLAN}=${plan}&${PARAMS_INTERVAL}=${interval}`;
  return fullPath ? window.location.origin + path : path;
};

export const GetAddonPaymentPageUrl = (
  status:
    | PaymentStatus.UPGRADE_SEATS_SUBSCRIPTION
    | PaymentStatus.DOWNGRADE_SEATS_SUBSCRIPTION
    | PaymentStatus.UPGRADE_PROJECTS_SUBSCRIPTION
    | PaymentStatus.DOWNGRADE_PROJECTS_SUBSCRIPTION,
  count: number,
  fullPath: boolean
) => {
  const path = `${PATH_DASHBOARD.payments}?${PARAMS_STATUS}=${status}&${PARAMS_COUNT}=${count}`;
  return fullPath ? window.location.origin + path : path;
};

export const GetProjectPaymentPageUrl = (
  status: PaymentStatus.CREATE_PROJECT_PURCHASE,
  fullPath: boolean,
  context?: { workspaceId?: string | null; projectId?: string | null }
) => {
  const params = new URLSearchParams({ [PARAMS_STATUS]: status });
  const workspaceId = context?.workspaceId?.trim();
  const projectId = context?.projectId?.trim();
  if (workspaceId) {
    params.set(PARAMS_WORKSPACE_ID, workspaceId);
  }
  if (projectId) {
    params.set(PARAMS_PROJECT_ID, projectId);
  }
  // Stripe substitutes the placeholder; do not encode braces.
  params.set(PARAMS_CHECKOUT_SESSION_ID, STRIPE_CHECKOUT_SESSION_ID_PLACEHOLDER);
  const path = `${PATH_DASHBOARD.payments}?${params.toString()}`.replace(
    encodeURIComponent(STRIPE_CHECKOUT_SESSION_ID_PLACEHOLDER),
    STRIPE_CHECKOUT_SESSION_ID_PLACEHOLDER
  );
  return fullPath ? window.location.origin + path : path;
};

const readOptionalStringParam = (
  searchParams: URLSearchParams,
  paramKey: string
): string | null => {
  const param = searchParams.get(paramKey)?.trim();
  return param ? param : null;
};

const readOptionalNumberParam = (
  searchParams: URLSearchParams,
  paramKey: string
): number | null => {
  const raw = searchParams.get(paramKey);
  if (raw == null || raw.trim() === "") {
    return null;
  }
  const param = Number(raw);
  return Number.isFinite(param) && param !== 0 ? param : null;
};

export const PaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read once — a new object each render would restart ProfileInfoLoader polling forever.
  const [storedCheckoutContext] = useState<OtpCheckoutContext | null>(() =>
    readOtpCheckoutContext()
  );
  const workspaceId =
    readOptionalStringParam(searchParams, PARAMS_WORKSPACE_ID) ||
    storedCheckoutContext?.workspaceId?.trim() ||
    null;
  const projectId =
    readOptionalStringParam(searchParams, PARAMS_PROJECT_ID) ||
    storedCheckoutContext?.projectId?.trim() ||
    null;
  const checkoutSessionId = readOptionalStringParam(searchParams, PARAMS_CHECKOUT_SESSION_ID);
  const status = readOptionalStringParam(searchParams, PARAMS_STATUS);
  const plan = readOptionalNumberParam(searchParams, PARAMS_PLAN);
  const interval = readOptionalStringParam(searchParams, PARAMS_INTERVAL);
  const count = readOptionalNumberParam(searchParams, PARAMS_COUNT);
  const baselineOtp = useMemo(
    () => storedCheckoutContext?.baseline ?? null,
    [storedCheckoutContext]
  );

  const isKnownPaymentStatus = useMemo(() => {
    if (!status) {
      return false;
    }
    return Object.values(PaymentStatus).includes(status as PaymentStatus);
  }, [status]);

  useEffect(() => {
    if (!status || !isKnownPaymentStatus) {
      navigate(PATH_OTHER.notFound, { replace: true });
    }
  }, [status, isKnownPaymentStatus, navigate]);

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const renderPaymentStatus = () => {
    if (!status || !isKnownPaymentStatus) {
      return null;
    }

    const isProfileReady = {
      planChange: (session: SessionUser) => {
        const activeMatches =
          session.pricing_plan?.pricing_plan_id === plan &&
          session.subscription?.interval === interval;
        const upcomingMatches =
          session.upcoming_pricing_plan?.pricing_plan_id === plan &&
          session.upcoming_pricing_plan?.interval === interval;
        const planMatchesIgnoreInterval =
          session.pricing_plan?.pricing_plan_id === plan ||
          session.upcoming_pricing_plan?.pricing_plan_id === plan;
        return activeMatches || upcomingMatches || planMatchesIgnoreInterval;
      },

      planDowngradeRequest: (session: SessionUser) =>
        session.upcoming_pricing_plan?.pricing_plan_id === plan &&
        session.upcoming_pricing_plan?.interval === interval,

      seatChange: (session: SessionUser) => session.subscription?.seats_purchased === count,

      projectsChange: (session: SessionUser) => session.subscription?.projects_purchased === count,

      projectPurchase: (session: SessionUser) =>
        isOtpPurchaseVerificationReady({ session, workspaceId }),

      projectTrial: (session: SessionUser) =>
        isOtpPurchaseVerificationReady({ session, workspaceId }),
    };

    switch (status) {
      case PaymentStatus.CREATE_PLAN_SUBSCRIPTION:
        return (
          <ProfileInfoLoader
            isProfileReady={isProfileReady.planChange}
            maxTries={10}
            delayMs={1000}
          >
            <PlanPurchaseSuccessResult />
          </ProfileInfoLoader>
        );
      case PaymentStatus.UPGRADE_PLAN_SUBSCRIPTION:
        return (
          <ProfileInfoLoader
            isProfileReady={isProfileReady.planChange}
            maxTries={10}
            delayMs={1000}
          >
            <UpgradeSuccessResult />
          </ProfileInfoLoader>
        );
      case PaymentStatus.DOWNGRADE_PLAN_SUBSCRIPTION:
        return (
          <ProfileInfoLoader isProfileReady={isProfileReady.planDowngradeRequest}>
            <PlanDowngradeRequestSuccessResult />
          </ProfileInfoLoader>
        );
      case PaymentStatus.CANCEL_DOWNGRADE_PLAN_SUBSCRIPTION:
        return (
          <ProfileInfoLoader isProfileReady={isProfileReady.planDowngradeRequest}>
            <PlanDowngradeCancelSuccessResult />
          </ProfileInfoLoader>
        );

      // Addon cases
      case PaymentStatus.UPGRADE_SEATS_SUBSCRIPTION:
        return (
          <ProfileInfoLoader isProfileReady={isProfileReady.seatChange}>
            <UpgradeSuccessResult />
          </ProfileInfoLoader>
        );
      case PaymentStatus.DOWNGRADE_SEATS_SUBSCRIPTION:
        return (
          <ProfileInfoLoader isProfileReady={isProfileReady.seatChange}>
            <DowngradeSuccessResult />
          </ProfileInfoLoader>
        );

      case PaymentStatus.UPGRADE_PROJECTS_SUBSCRIPTION:
        return (
          <ProfileInfoLoader isProfileReady={isProfileReady.projectsChange}>
            <UpgradeSuccessResult />
          </ProfileInfoLoader>
        );
      case PaymentStatus.DOWNGRADE_PROJECTS_SUBSCRIPTION:
        return (
          <ProfileInfoLoader isProfileReady={isProfileReady.projectsChange}>
            <DowngradeSuccessResult />
          </ProfileInfoLoader>
        );

      // Project Purchase cases
      case PaymentStatus.CREATE_PROJECT_PURCHASE:
        return (
          <ProfileInfoLoader
            isProfileReady={isProfileReady.projectPurchase}
            pollQuotas
            workspaceId={workspaceId}
            projectId={projectId}
            checkoutSessionId={checkoutSessionId}
            baselineOtp={baselineOtp}
            maxTries={OTP_PURCHASE_POLL_MAX_TRIES}
            delayMs={OTP_PURCHASE_POLL_DELAY_MS}
          >
            <ProjectPurchaseSuccessResult
              sessionId={checkoutSessionId}
              workspaceId={workspaceId}
              projectId={projectId}
            />
          </ProfileInfoLoader>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout
      title={`Payment ${status !== null ? capitalize(status) : ""}`}
      showSidebar={false}
      showPlanUpgradeButton={false}
    >
      <div className="box min-h-screen flex flex-col justify-center items-center relative w-full">
        {renderPaymentStatus()}
      </div>
    </DashboardLayout>
  );
};
